import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, orderBy, query, where } from 'firebase/firestore';
import Navbar from '../Components/Navbar/Navbar';
import Footer from '../Components/Footer/Footer';
// import HubMap from '../Components/HubMap/HubMap';
import { db } from '../firebase';
import './Connect.css';

import bannerImage from '../Images/Connect/Banner.jpg';
export default function Connect() {
  const [businesses, setBusinesses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [islands, setIslands] = useState([]);
  const [countries, setCountries] = useState([]);
  const [businessSearch, setBusinessSearch] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [islandFilter, setIslandFilter] = useState('');
  const [islandLabel, setIslandLabel] = useState('');
  const [showIslandMenu, setShowIslandMenu] = useState(false);
  const [activeIslandGroup, setActiveIslandGroup] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const islandDropdownRef = useRef(null);

  useEffect(() => {
    async function loadDirectoryData() {
      try {
        const [businessSnapshot, categorySnapshot, islandSnapshot, countrySnapshot] = await Promise.all([
          getDocs(query(collection(db, 'businesses'), where('is_published', '==', true), orderBy('business_name'))),
          getDocs(query(collection(db, 'categories'), where('enabled', '==', true), orderBy('name'))),
          getDocs(collection(db, 'islands')),
          getDocs(collection(db, 'country')),
        ]);

        setBusinesses(businessSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
        setCategories(categorySnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
        setIslands(islandSnapshot.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .filter((island) => island.enabled !== false && island.name)
          .sort((a, b) => a.name.localeCompare(b.name)));
        setCountries(countrySnapshot.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .filter((country) => country.enabled !== false && country.name)
          .sort((a, b) => a.name.localeCompare(b.name)));
      } catch (loadError) {
        console.error('Failed to load Connect directory data:', loadError);
        setError('We could not load the business directory. Please try again shortly.');
      } finally {
        setLoading(false);
      }
    }

    loadDirectoryData();
  }, []);

  useEffect(() => {
    function closeIslandMenu(event) {
      if (islandDropdownRef.current && !islandDropdownRef.current.contains(event.target)) {
        setShowIslandMenu(false);
        setActiveIslandGroup('');
      }
    }
    document.addEventListener('mousedown', closeIslandMenu);
    return () => document.removeEventListener('mousedown', closeIslandMenu);
  }, []);

  const { islandGroups, standaloneIslands } = useMemo(
    () => buildIslandMenu(islands, countries),
    [islands, countries]
  );

  const selectedLocationTerms = useMemo(() => {
    const selectedGroup = islandGroups.find(
      (group) => normalizeLocation(group.label) === normalizeLocation(islandFilter)
    );
    if (!selectedGroup) return getSelectedLocationTerms(islandFilter);
    return [
      ...getSelectedLocationTerms(selectedGroup.label),
      ...selectedGroup.options.flatMap(([, value]) => getSelectedLocationTerms(value)),
    ];
  }, [islandFilter, islandGroups]);

  const filteredBusinesses = useMemo(() => businesses.filter((business) => {
    const nameMatches = !businessSearch.trim() || business.business_name
      ?.toLowerCase()
      .includes(businessSearch.trim().toLowerCase());
    const categoryMatches = !categoryId || business.categoryIds?.includes(categoryId);
    const locationMatches = !islandFilter || getBusinessLocations(business).some((location) =>
      [location.island, location.country].filter(Boolean).some((name) =>
        selectedLocationTerms.includes(normalizeLocation(name))
      )
    );
    return nameMatches && categoryMatches && locationMatches;
  }), [businessSearch, businesses, categoryId, islandFilter, selectedLocationTerms]);

  function selectIsland(value, label = formatLocationName(value), scrollToResults = true) {
    setIslandFilter(value);
    setIslandLabel(label);
    setShowIslandMenu(false);
    setActiveIslandGroup('');
    if (scrollToResults) {
      window.setTimeout(() => document.getElementById('connect-results')?.scrollIntoView({ behavior: 'smooth' }), 0);
    }
  }

  function clearFilters() {
    setBusinessSearch('');
    setCategoryId('');
    setIslandFilter('');
    setIslandLabel('');
    setShowIslandMenu(false);
    setActiveIslandGroup('');
  }

  return (
    <div className="connect-page">
      <Navbar />

      <header className="connect-hero">
        <img className="connect-banner-image" src={bannerImage} alt="Caribbean yacht services" />
        <div className="connect-hero-overlay" />
        <div className="connect-hero-content">
          <h1>Connect with Other Services Across the Caribbean</h1>
        </div>
      </header>

      <main className="connect-main">
        <section className="connect-intro">
          <span className="connect-kicker">Caribbean Business Directory</span>
          <h2>What are you looking for?</h2>
          {/* <p>
            Search by business, category or service, and island or location.
          </p> */}
        </section>

        <section className="connect-search-panel" aria-label="Business search filters">
          <label>
            <span>Search by business</span>
            <input
              type="search"
              value={businessSearch}
              onChange={(event) => setBusinessSearch(event.target.value)}
              placeholder="Business name"
            />
          </label>
          <label>
            <span>Search by category/service</span>
            <select value={categoryId} onChange={(event) => setCategoryId(event.target.value)}>
              <option value="">All categories and services</option>
              {categories.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}
            </select>
          </label>
          <div className="connect-filter-field">
            <span>Search by island/location</span>
            <div className="connect-island-dropdown" ref={islandDropdownRef}>
              <button
                type="button"
                className="connect-island-button"
                aria-haspopup="menu"
                aria-expanded={showIslandMenu}
                onClick={() => setShowIslandMenu((open) => !open)}
              >
                <span>{islandLabel || 'Search all Islands'}</span>
                <span className="connect-island-chevron" aria-hidden="true">▾</span>
              </button>
              {showIslandMenu && (
                <div className="connect-island-menu" role="menu">
                  <button type="button" role="menuitem" onClick={() => selectIsland('', '', false)}>
                    Search all Islands
                  </button>
                  {islandGroups.map((group) => (
                    <div
                      className={`connect-island-group ${activeIslandGroup === group.label ? 'is-active' : ''}`}
                      key={group.label}
                      onMouseEnter={() => setActiveIslandGroup(group.label)}
                      onMouseLeave={() => setActiveIslandGroup('')}
                    >
                      <button
                        type="button"
                        className="connect-island-group-button"
                        aria-haspopup="menu"
                        aria-expanded={activeIslandGroup === group.label}
                        onClick={() => setActiveIslandGroup((active) => active === group.label ? '' : group.label)}
                        onFocus={() => setActiveIslandGroup(group.label)}
                      >
                        <span>{group.label}</span><span aria-hidden="true">›</span>
                      </button>
                      {activeIslandGroup === group.label && (
                        <div className="connect-island-submenu" role="menu" aria-label={group.label}>
                          <button
                            type="button"
                            role="menuitem"
                            className="connect-island-group-all"
                            onClick={() => selectIsland(group.label, `All ${group.label}`, false)}
                          >
                            All {group.label}
                          </button>
                          {group.options.map(([label, value]) => (
                            <button
                              type="button"
                              role="menuitem"
                              key={`${group.label}-${label}`}
                              onClick={() => selectIsland(value, `${group.label} : ${label}`, false)}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  {standaloneIslands.map((name) => (
                    <button type="button" role="menuitem" key={name} onClick={() => selectIsland(name, formatLocationName(name), false)}>
                      {formatLocationName(name)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <button type="button" onClick={clearFilters}>Clear filters</button>
        </section>

        {/* <section className="connect-map-section" aria-labelledby="connect-map-title">
          <div className="connect-section-heading">
            <span className="connect-kicker">Explore the Caribbean</span>
            <h2 id="connect-map-title">Select an island on the map</h2>
            <p>Click a map marker to automatically filter the businesses shown below.</p>
          </div>
          <div className="connect-map-wrapper"><HubMap onIslandSelect={selectIsland} /></div>
        </section> */}

        <section id="connect-results" className="connect-results" aria-live="polite">
          <div className="connect-results-heading">
            <div>
              <span className="connect-kicker">Business results</span>
              <h2>{islandLabel ? `Businesses in ${islandLabel}` : 'Available businesses'}</h2>
            </div>
            {!loading && !error && <strong>{filteredBusinesses.length} result{filteredBusinesses.length === 1 ? '' : 's'}</strong>}
          </div>

          {loading && <div className="connect-status">Loading businesses…</div>}
          {error && <div className="connect-status connect-status-error">{error}</div>}
          {!loading && !error && filteredBusinesses.length === 0 && (
            <div className="connect-status">No businesses match the selected filters.</div>
          )}
          {!loading && !error && filteredBusinesses.length > 0 && (
            <div className="connect-business-list">
              {filteredBusinesses.map((business) => (
                <Link key={business.id} to={`/business/${business.id}`} className="connect-business-card">
                  <h3>{business.business_name}</h3>
                  <span>{getLocationSummary(business).join(', ') || 'Caribbean-wide service'}</span>
                  {business.about_short && <p>{business.about_short.split(/\s+/).slice(0, 36).join(' ')}</p>}
                </Link>
              ))}
            </div>
          )}
        </section>

      </main>

      <Footer />
    </div>
  );
}

function getBusinessLocations(business) {
  if (Array.isArray(business.locations) && business.locations.length > 0) {
    return business.locations.filter(Boolean);
  }
  return [{
    country: business.country || '',
    island: business.island || '',
    city: business.city || '',
    address: business.locationLabel || business.location?.address || '',
  }];
}

function normalizeLocation(value) {
  return String(value || '').toLowerCase().replace(/\bsaint\b/g, 'st').replace(/[^a-z0-9]/g, '');
}

function formatLocationName(value = '') {
  return value.replace(/\bSaint\b/gi, 'St.');
}

function getSelectedLocationTerms(value) {
  if (value === 'Nassau/New Providence') {
    return ['nassau', 'newprovidence', 'nassaunewprovidence'];
  }
  return value ? [normalizeLocation(value)] : [];
}

function buildIslandMenu(islands, countries) {
  const usedNames = new Set();
  const standaloneByName = new Map();
  const islandGroups = [];

  countries.forEach((country) => {
    const slugNames = (Array.isArray(country.slugs) ? country.slugs : [])
      .map((entry) => typeof entry === 'object' ? entry.name || entry.slug : entry)
      .filter(Boolean)
      .map(String);
    const uniqueSlugs = [...new Map(
      slugNames.map((name) => [normalizeLocation(name), name])
    ).values()];
    const childIslands = uniqueSlugs.filter(
      (name) => normalizeLocation(name) !== normalizeLocation(country.name)
    );

    if (childIslands.length > 0) {
      childIslands.forEach((name) => usedNames.add(normalizeLocation(name)));
      usedNames.add(normalizeLocation(country.name));
      islandGroups.push({
        label: formatLocationName(country.name),
        options: childIslands
          .sort((a, b) => a.localeCompare(b))
          .map((name) => [formatLocationName(name), name]),
      });
      return;
    }

    const standaloneName = uniqueSlugs[0] || country.name;
    standaloneByName.set(normalizeLocation(standaloneName), standaloneName);
    usedNames.add(normalizeLocation(standaloneName));
  });

  islands.forEach((island) => {
    const key = normalizeLocation(island.name);
    if (!usedNames.has(key)) standaloneByName.set(key, island.name);
  });

  islandGroups.sort((a, b) => a.label.localeCompare(b.label));
  return {
    islandGroups,
    standaloneIslands: [...standaloneByName.values()].sort((a, b) => a.localeCompare(b)),
  };
}

function getLocationSummary(business) {
  return [...new Set(getBusinessLocations(business)
    .map((location) => location.island || location.country || location.city || location.address)
    .filter(Boolean))];
}
